import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ImageData } from "../types";
import {
  MediaApiError,
  uploadTeamLogo,
  deleteTeamLogo,
  uploadPlayerPhoto,
  deletePlayerPhoto,
} from "../lib/loggerApi";

const MAX_MEDIA_SIZE = 5 * 1024 * 1024; // 5MB — mirrors the backend player-photo limit
// 1MB — mirrors the backend's MAX_LOGO_SVG_BYTES (SVGs are text, far smaller than raster photos)
const TEAM_LOGO_MAX_SIZE = 1 * 1024 * 1024;
// Team logos are SVG-only (hard cutover). PNG/JPEG/etc are no longer accepted.
const TEAM_LOGO_ACCEPTED_TYPES = new Set(["image/svg+xml"]);
const TEAM_LOGO_ACCEPTED_EXTENSIONS = new Set([".svg"]);
const TEAM_LOGO_ACCEPT_STRING = "image/svg+xml,.svg";
const PLAYER_PHOTO_ACCEPTED_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);
const PLAYER_PHOTO_ACCEPTED_EXTENSIONS = new Set([
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
]);
const PLAYER_PHOTO_ACCEPT_STRING =
  "image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp";

export type MediaKind = "team-logo" | "player-photo";

interface ImageUploadProps {
  /** Which upload endpoint/validation rules apply. */
  kind: MediaKind;
  /** team_id or player_id. Upload is disabled until the entity is persisted. */
  entityId?: string;
  /** Current logo_url / photo_url from the server, if any. */
  url?: string | null;
  /** Legacy inline base64 image, rendered only when `url` is absent. */
  legacyImage?: ImageData | null;
  onUploaded: (url: string) => void;
  onDeleted: () => void;
  /** Whether the current user is allowed to upload/replace/delete this image. */
  canEdit: boolean;
  label?: string;
  size?: number;
}

function getInitials(label?: string): string {
  if (!label) return "?";
  const parts = label.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
}

function toDataUrl(image: ImageData): string {
  return `data:${image.mime_type};base64,${image.data}`;
}

function hasAcceptedExtension(
  fileName: string,
  extensions: Set<string>,
): boolean {
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex === -1) return false;
  return extensions.has(fileName.slice(dotIndex).toLowerCase());
}

function validateFile(
  kind: MediaKind,
  file: File,
  t: (key: string, fallback: string) => string,
): string | null {
  const acceptedTypes =
    kind === "team-logo"
      ? TEAM_LOGO_ACCEPTED_TYPES
      : PLAYER_PHOTO_ACCEPTED_TYPES;
  const acceptedExtensions =
    kind === "team-logo"
      ? TEAM_LOGO_ACCEPTED_EXTENSIONS
      : PLAYER_PHOTO_ACCEPTED_EXTENSIONS;
  // Some browsers report file.type === "" for otherwise-valid files
  // (observed on macOS/Chrome); fall back to the extension in that case.
  const isAccepted = file.type
    ? acceptedTypes.has(file.type)
    : hasAcceptedExtension(file.name, acceptedExtensions);
  if (!isAccepted) {
    return kind === "team-logo"
      ? t("mediaUpload.errorTeamLogoType", "Only SVG files are accepted")
      : t(
          "mediaUpload.errorPlayerPhotoType",
          "Only JPEG, PNG, or WebP files are accepted",
        );
  }
  if (kind === "team-logo") {
    if (file.size > TEAM_LOGO_MAX_SIZE) {
      return t("mediaUpload.errorLogoFileTooLarge", "File exceeds 1MB limit");
    }
  } else if (file.size > MAX_MEDIA_SIZE) {
    return t("mediaUpload.errorFileTooLarge", "File exceeds 5MB limit");
  }
  return null;
}

export default function ImageUpload({
  kind,
  entityId,
  url,
  legacyImage,
  onUploaded,
  onDeleted,
  canEdit,
  label,
  size = 80,
}: ImageUploadProps) {
  const { t } = useTranslation("admin");
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  // A new/replaced upload gets a fresh attempt at rendering even if the
  // previous url 404'd (fake storage in e2e/local, or a transient 404 before
  // CDN propagation in prod).
  useEffect(() => {
    setBroken(false);
  }, [url]);

  const disabled = !canEdit || !entityId;

  const processFile = useCallback(
    async (file: File) => {
      if (!entityId) return;
      setError(null);

      const validationError = validateFile(kind, file, t);
      if (validationError) {
        setError(validationError);
        return;
      }

      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      setLoading(true);
      try {
        if (kind === "team-logo") {
          const result = await uploadTeamLogo(entityId, file);
          onUploaded(result.logo_url);
        } else {
          const result = await uploadPlayerPhoto(entityId, file);
          onUploaded(result.photo_url);
        }
      } catch (err) {
        setError(
          err instanceof MediaApiError
            ? err.message
            : t("mediaUpload.errorUploadFailed", "Upload failed"),
        );
      } finally {
        setLoading(false);
        setPreviewUrl((current) => {
          if (current) URL.revokeObjectURL(current);
          return null;
        });
      }
    },
    [kind, entityId, onUploaded, t],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled || loading) return;
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    },
    [disabled, loading, processFile],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) processFile(file);
      if (inputRef.current) inputRef.current.value = "";
    },
    [processFile],
  );

  const handleRemove = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!entityId || loading) return;
      setError(null);
      setLoading(true);
      try {
        if (kind === "team-logo") {
          await deleteTeamLogo(entityId);
        } else {
          await deletePlayerPhoto(entityId);
        }
        onDeleted();
      } catch (err) {
        setError(
          err instanceof MediaApiError
            ? err.message
            : t("mediaUpload.errorDeleteFailed", "Failed to remove image"),
        );
      } finally {
        setLoading(false);
      }
    },
    [kind, entityId, loading, onDeleted, t],
  );

  const persistedSrc = url ?? (legacyImage ? toDataUrl(legacyImage) : null);
  const displaySrc = previewUrl ?? (broken ? null : persistedSrc);
  const acceptString =
    kind === "team-logo" ? TEAM_LOGO_ACCEPT_STRING : PLAYER_PHOTO_ACCEPT_STRING;
  const hintText = kind === "team-logo" ? "SVG" : "JPG/PNG/WebP";

  return (
    <div className="flex flex-col items-center gap-1">
      {label && (
        <span className="text-xs text-gray-500 font-medium">{label}</span>
      )}
      <div
        data-testid="image-upload-zone"
        className={`relative flex items-center justify-center border-2 border-dashed rounded-lg transition-colors ${
          dragOver
            ? "border-blue-400 bg-blue-50"
            : displaySrc
              ? "border-gray-200 bg-white"
              : "border-gray-300 bg-gray-50 hover:border-gray-400"
        } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        style={{ width: size, height: size }}
        onClick={() => !disabled && !loading && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled && !loading) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        {loading ? (
          <svg
            className="animate-spin h-5 w-5 text-gray-400"
            viewBox="0 0 24 24"
            data-testid="image-upload-spinner"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        ) : displaySrc ? (
          <>
            <img
              src={displaySrc}
              alt={label ?? ""}
              className="max-w-full max-h-full object-contain rounded"
              style={{ maxWidth: size - 8, maxHeight: size - 8 }}
              data-testid="image-upload-preview"
              onError={() => setBroken(true)}
            />
            {!disabled && (
              <button
                type="button"
                onClick={handleRemove}
                className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 shadow"
                data-testid="image-upload-remove"
                title={t("removeImage", "Remove image")}
              >
                ×
              </button>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center text-gray-400 select-none">
            <svg
              className="w-5 h-5 mb-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span className="text-[10px] leading-tight text-center">
              {hintText}
            </span>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={acceptString}
          onChange={handleFileChange}
          className="hidden"
          data-testid="image-upload-input"
          disabled={disabled || loading}
        />
      </div>
      {canEdit && !entityId && (
        <p
          className="text-[10px] text-gray-400 text-center max-w-[120px]"
          data-testid="image-upload-save-first-hint"
        >
          {t("mediaUpload.saveFirstHint", "Save first to add an image")}
        </p>
      )}
      {error && (
        <p
          className="text-[10px] text-red-500 text-center max-w-[120px]"
          data-testid="image-upload-error"
        >
          {error}
        </p>
      )}
    </div>
  );
}

export { toDataUrl, getInitials };
export type { ImageUploadProps };
